// @ts-nocheck

import {
  Suspense,
  forwardRef,
  lazy,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FlightDetailsHeader } from "Components/molecules/FlightDetailsHeader/Desktop";
import { StopsFilter } from "Components/molecules/StopsFilter";
import { FlightCard } from "Components/organisms/CardFlight/Desktop";
import { FlightCardMobile } from "Components/organisms/CardFlight/Mobile";
import { AirportsFilter } from "Components/molecules/AirportsFilter";
import { AirportsFilterItem } from "Components/molecules/AirportsFilter/types";
import { EntityFilter } from "Components/molecules/EntityFilter";
import { EntityFilterItem } from "Components/molecules/EntityFilter/types";
import { RangeFilter } from "Components/molecules/PriceRangeFilter";
import {
  ContainerResultsFlights,
  StyledFilterContainer,
  StyledFlightCardsContainer,
  StyledResultsFlights,
  StyledResultsHeader,
  StyledRigthAdsContainer,
} from "./styles";
import { useSearchContext } from "shared/contexts/SearchContext";
import {
  SearchFlightsResult,
  useFlightSearch,
} from "shared/hooks/useFlightSearch";
import { useFilterContext } from "shared/contexts/FilterContext";
import { CreateSearchIntentResponse } from "shared/services/flights/createSearchIntentResponse";
import { SERPResult } from "../../../shared/types/serpResult";
import {
  getSellerImgById,
  orderResultsByBestOption,
  orderResultsByPrice,
  parseCreateSearchIntentToSERPResult,
} from "./parser";
import { matchesAirlineFilter } from "./airlineFilter";
import PaginationComponent from "Components/molecules/Pagination/Desktop";
import { getAirports } from "shared/services/contexts/airport.service";
import ProgressBar from "Components/molecules/ProgressBar";
import { SearchOrder } from "Components/molecules/ProgressBar/types";
import { useInlineBanner } from "../../../shared/hooks/useInlineBanner";
import InlineBanner from "../../molecules/InLineBanner";
import { AdBannerRightContentContainerDesktop } from "Components/molecules/AdBannerRightContent/Desktop";
import FlightCardSkeleton from "Components/atoms/Skeleton/FlightCardSkeleton";
import airlinesData from "shared/config/airlines/airlines.json";
import SelectButton from "Components/atoms/SelectButton";
import AlertSerpCta from "Components/molecules/AlertSerpCta";
import HeaderFilterMobile from "Components/organisms/HeaderFilterMobile";
import { useBreakpoint } from "shared/hooks/useBreakpoint";
import { FilterModal } from "Components/molecules/FilterModal";
import { AdBannerContainer } from "Components/molecules/AdBanner";
import pollingConfig from "shared/config/pollingConfig.json";
import { captureSentryException } from "shared/services/sentry/captureException";
import { useLocation } from "react-router-dom";
import { shouldStopPolling } from "shared/hooks/searchPollingFailure";
import {
  filterPastDepartureDates,
  formatIsoDateToBr,
  hasOnlyPastDepartureDates,
} from "shared/utils/searchDates";
import { SearchErrorContent } from "Components/molecules/SearchErrorContent";

const NoFlightsContent = lazy(
  () => import("Components/molecules/NoFlightsContent"),
);

const DEFAULT_SEARCH_POLLING_INTERVAL_MS = 3000;

const getResultDuration = (result) =>
  result.sectors.reduce(
    (total, sector) => total + (sector.totalDuration || 0),
    0,
  );

const serializeDate = (value: Date | string) =>
  value instanceof Date ? value.toISOString() : String(value);

export const getFlightResultKey = (result) =>
  result.sectors
    .flatMap((sector) =>
      sector.flights.map((flight) =>
        [
          flight.airline,
          flight.flightNumber,
          flight.departureAirportIata,
          flight.arrivalAirportIata,
          serializeDate(flight.departureDate),
          serializeDate(flight.departureTime),
          serializeDate(flight.arrivalDate),
          serializeDate(flight.arrivalTime),
        ].join("-"),
      ),
    )
    .join("_");

export const ResultsFlightsTemplate = forwardRef<HTMLDivElement>((_, ref) => {
  const location = useLocation();
  const {
    searchState,
    setSearchLoader,
    setStateSearchFinished,
    setSearchPercentResult,
    filteredFlightResults,
    setFilteredFlightResults,
  } = useSearchContext();

  useEffect(() => {
    console.log(
      "[test]: 2.1) updated filteredFlightResults from context",
      filteredFlightResults,
    );
  }, [filteredFlightResults]);

  const {
    stops,
    setStops,
    minPrice,
    setMinPrice,
    maxPrice,
    setMaxPrice,
    priceRange,
    setPriceRange,
    airportsFrom,
    setAirportsFrom,
    airportsTo,
    setAirportsTo,
    firstSectorTimeRange,
    setFirstSectorTimeRange,
    secondSectorTimeRange,
    setSecondSectorTimeRange,
    airlines,
    setAirlines,
    sellers,
    setSellers,
  } = useFilterContext();

  const { searchFlights, data } = useFlightSearch();
  const { inlineData } = useInlineBanner({
    originAirport: searchState.origin?.code ?? "",
    destinationAirport: searchState.destination?.code ?? "",
    departureDates: searchState.departureDates,
    returnDates: searchState.returnDates,
    adults: searchState.adults,
    children: searchState.children,
    babies: searchState.babies,
  });
  const [flightResults, setFlightResults] = useState<SERPResult | null>(null);
  const searchExecuted = useRef(false);
  const executedSearchKeyRef = useRef<string | null>(null);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingGenerationRef = useRef(0);
  const pollingRequestRef = useRef<Promise<SearchFlightsResult> | null>(null);
  const pollingRequestGenerationRef = useRef(0);
  const [isSearchFinished, setIsSearchFinished] = useState(false);
  const [searchFailure, setSearchFailure] = useState<
    "past-date" | "request-failed" | null
  >(null);
  const [searchRetryNonce, setSearchRetryNonce] = useState(0);
  const pastDepartureDateLabel = formatIsoDateToBr(
    searchState.departureDates?.[0] ?? "",
  );
  const dataRef = useRef<CreateSearchIntentResponse | null>(null);
  const [desktop, setDesktop] = useState(false);
  const [resultOrder, setResultOrder] = useState<SearchOrder>("cheapest");
  const breakpoint = useBreakpoint();
  const [isVisibleFilterModal, setIsVisibleFilterModal] = useState(false);
  const headerFilterRef = useRef<HTMLDivElement | null>(null);

  const parsePollingNumber = (value: string | number, fallback: number) => {
    const parsed = Number(value);

    return Number.isFinite(parsed) && parsed > 0
      ? Math.trunc(parsed)
      : fallback;
  };

  const getPollingControl = (config: any) => {
    return config.general.polling_control;
  };

  const getProgressiveDelayControl = (config: any) => {
    return config.general.polling_control.progressive_delay;
  };

  const getDateCombinationCount = (
    departureDates: string[] | null,
    returnDates: string[] | null,
  ) => {
    const departureDatesCount = Math.max(departureDates?.length ?? 0, 1);
    const returnDatesCount = Math.max(returnDates?.length ?? 0, 1);

    return departureDatesCount * returnDatesCount;
  };

  const getPollingRequestLimit = (
    departureDates: string[] | null,
    returnDates: string[] | null,
    config: any,
  ) => {
    const pollingControl = getPollingControl(config);
    const defaultPolls = parsePollingNumber(
      pollingControl.default_quantify_polls,
      15,
    );

    if (pollingControl.is_enabled !== "1") {
      return defaultPolls;
    }

    const minPolls = parsePollingNumber(pollingControl.min_quantify_polls, 5);
    const additionalPolls = parsePollingNumber(
      pollingControl.additional_quantify_polls,
      0,
    );
    const departureDatesCount = departureDates?.length ?? 0;
    const returnDatesCount = returnDates?.length ?? 0;
    const isSingleDateSearch =
      departureDatesCount <= 1 && returnDatesCount <= 1;

    if (isSingleDateSearch) {
      return minPolls;
    }

    const progressiveDelayControl = getProgressiveDelayControl(config);
    const rateRequestDelay = parsePollingNumber(
      progressiveDelayControl.rate_request_delay,
      1000,
    );
    const rateRequestDelayInSeconds = rateRequestDelay / 1000;

    return Math.ceil(
      (getDateCombinationCount(departureDates, returnDates) - 1) *
        rateRequestDelayInSeconds +
        additionalPolls,
    );
  };

  const getPollingInterval = (config: any) => {
    const pollingControl = getPollingControl(config);

    return parsePollingNumber(
      pollingControl.polling_interval_seconds,
      DEFAULT_SEARCH_POLLING_INTERVAL_MS,
    );
  };

  useEffect(() => {
    setDesktop(
      breakpoint === "xxl" || breakpoint === "xl" || breakpoint === "lg",
    );
  }, [breakpoint]);

  useEffect(() => {
    if (!desktop) {
      headerFilterRef.current?.setAttribute("id", "header-filter-mobile");
    }
  }, [desktop]);

  useEffect(() => {
    const loadAirlines = async () => {
      const parsedAirlines = await parseAirlines(
        filteredFlightResults?.predefinedFilters?.airlines ?? [],
      );

      if (Array.isArray(parsedAirlines)) {
        setAirlines(parsedAirlines);
      }
    };

    searchState.searchPercentResult < 100 && loadAirlines();
  }, [filteredFlightResults]);

  // #################### --Filtros-- ####################

  // #################### CONEXÕES ####################
  const stopsOptions = [
    { label: "Direto", value: 0 },
    { label: "1 Parada", value: 1 },
    { label: "2 Paradas", value: 2 },
    { label: "3+ Paradas", value: 3 },
  ];
  const handleStopsChange = (updatedStops: number[]) => {
    setStops(updatedStops);
  };
  // #################### PREÇO ####################

  // const [priceRange, setPriceRange] = useState<[number, number]>([
  //   filteredFlightResults?.predefinedFilters.priceFrom ?? 100,
  //   filteredFlightResults?.predefinedFilters.priceTo ?? 10000,
  // ]);

  // const [minPrice, setMinPrice] = useState<number>(priceRange[0]);
  // const [maxPrice, setMaxPrice] = useState<number>(priceRange[1]);

  const handlePriceChange = (values: [number, number]) => {
    console.log(`[handlePriceChange]: ${values}`);
    setPriceRange(values);
  };

  // #################### AEROPORTOS ####################

  const { findAirportByCode } = getAirports();
  const parseAirports = (airports: string[]): AirportsFilterItem[] => {
    const parsedAirports = [] as AirportsFilterItem[];

    if (airports && airports.length > 0) {
      for (let index = 0; index < airports.length; index++) {
        const airport = airports[index];

        parsedAirports.push({
          iata: airport,
          name: findAirportByCode(airport)?.name ?? "",
          isChecked: true,
        });
      }
    }

    return parsedAirports;
  };
  const [airportsOrigin, setAirportsOrigin] = useState<AirportsFilterItem[]>(
    [],
  );
  const [airportsDestination, setAirportsDestination] = useState<
    AirportsFilterItem[]
  >([]);

  const handleAirportOriginChange = (
    updatedAirport: AirportsFilterItem,
    isChecked: boolean,
  ) => {
    const updatedAirports = airportsFrom.map((airport) =>
      airport.iata === updatedAirport.iata
        ? { ...airport, isChecked }
        : airport,
    );

    setAirportsFrom(updatedAirports);
  };

  const handleAllAirportOrigins = (isChecked: boolean) => {
    const updatedAirports = airportsFrom.map((airport) => ({
      ...airport,
      isChecked,
    }));
    setAirportsFrom(updatedAirports);
  };

  const handleAirportDestinationChange = (
    updatedAirport: AirportsFilterItem,
    isChecked: boolean,
  ) => {
    // Atualiza o isChecked da lista de AirportsFilterItem
    const updatedAirports = airportsTo.map((airport) =>
      airport.iata === updatedAirport.iata
        ? { ...airport, isChecked }
        : airport,
    );

    // Atualiza o estado local de aeroportos de destino
    setAirportsTo(updatedAirports);
  };

  const handleAllAirportDestinations = (isChecked: boolean) => {
    const updatedAirports = airportsTo.map((airport) => ({
      ...airport,
      isChecked,
    }));
    setAirportsTo(updatedAirports);
  };

  // #################### HORÁRIO DE IDA ####################

  const handleFisrtSectorDepartureTimeChange = (values: [number, number]) => {
    setFirstSectorTimeRange(values); // Atualiza o estado global no contexto
  };

  // #################### HORÁRIO DE VOLTA ####################

  const handleSecondSectorDepartureTimeChange = (values: [number, number]) => {
    setSecondSectorTimeRange(values); // Atualiza o estado global no contexto
  };

  // #################### COMPANHIAS AÉREAS ####################

  // Função síncrona, sem async
  // Função para verificar se uma imagem é válida de forma assíncrona
  const isImageValid = async (url: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = url;
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
    });
  };

  // Função para parsear as companhias aéreas de forma assíncrona
  const parseAirlines = async (
    airlines: string[],
  ): Promise<EntityFilterItem[]> => {
    const parsedItems: EntityFilterItem[] = [];

    for (const airline of airlines) {
      const logoUrl = `https://voopter.com.br/voopter-static-assets/airline-logos/${airline}.png`;

      const isValid = await isImageValid(logoUrl);

      parsedItems.push({
        id: airline,
        name: airlinesData.filter(
          (airlineItem) => airlineItem.id === airline,
        )[0]?.name,
        isChecked: true,
        logo: isValid ? logoUrl : "https://placehold.co/120x60/000000/2789B3",
      });
    }

    return parsedItems.sort((a, b) => a?.name?.localeCompare(b?.name));
  };

  const handleAirlineChange = (
    updated: EntityFilterItem,
    isChecked: boolean,
  ) => {
    // Atualiza o estado global do contexto (airlines)
    const updatedAirlines = airlines.map((airline) =>
      airline.id === updated.id ? { ...airline, isChecked } : airline,
    );

    if (Array.isArray(updatedAirlines)) {
      setAirlines(updatedAirlines);
    }
  };

  const handleAllAirlines = (isChecked: boolean) => {
    const updatedAirlines = airlines.map((airline) => ({
      ...airline,
      isChecked,
    }));
    setAirlines(updatedAirlines);
  };

  // #################### VENDEDORES ####################

  const parseSellers = (sellers: string[]): EntityFilterItem[] => {
    console.log("[Debug]: 4) Chamando parseSellers com sellers:", sellers);
    let parsedItems = [] as EntityFilterItem[];

    if (sellers && sellers.length > 0) {
      for (let index = 0; index < sellers.length; index++) {
        const seller = sellers[index];
        const logoUrl = flightResults?.results
          ? getSellerImgById(flightResults?.results, seller)
          : null;

        console.log(`[Debug]: Seller: ${seller}, Logo URL: ${logoUrl}`);

        parsedItems.push({
          id: seller,
          name: seller,
          isChecked: true,
          logo: logoUrl ?? "https://placehold.co/120x60/000000/2789B3",
        });
      }
    }

    parsedItems = parsedItems.sort((a, b) => a.name.localeCompare(b.name));
    return parsedItems;
  };

  const handleSellerChange = (
    updated: EntityFilterItem,
    isChecked: boolean,
  ) => {
    setSellers(
      sellers.map((seller) =>
        seller.id === updated.id ? { ...seller, isChecked } : seller,
      ),
    );
  };

  const handleAllSellers = (isChecked: boolean) => {
    const updatedSellers = sellers.map((seller) => ({
      ...seller,
      isChecked,
    }));
    setSellers(updatedSellers);
  };

  useEffect(() => {
    if (!desktop) return;

    const debounceTimer = setTimeout(() => {
      setFilteredFlightResults((prev: any) => ({
        ...prev,
        appliedFilters: {
          ...prev?.appliedFilters,
          stops,
          priceFrom: priceRange[0],
          priceTo: priceRange[1],
          airportsFrom: airportsFrom
            .filter((airport) => airport.isChecked)
            .map((airport) => airport.iata),
          airportsTo: airportsTo
            .filter((airport) => airport.isChecked)
            .map((airport) => airport.iata),
          firtsSectorDepartureTimeFrom: firstSectorTimeRange[0],
          firtsSectorDepartureTimeTo: firstSectorTimeRange[1],
          secondSectorDepartureTimeFrom: secondSectorTimeRange[0],
          secondSectorDepartureTimeTo: secondSectorTimeRange[1],
          airlines: airlines
            .filter((airline) => airline.isChecked)
            .map((airline) => airline.id),
          sellers: sellers
            .filter((seller) => seller.isChecked)
            .map((seller) => seller.id),
        },
        pagination: {
          ...prev?.pagination,
          currentPage: 1,
        },
      }));
    }, 800);

    return () => clearTimeout(debounceTimer);
  }, [
    desktop,
    stops,
    priceRange,
    airportsFrom,
    airportsTo,
    firstSectorTimeRange,
    secondSectorTimeRange,
    airlines,
    sellers,
    setFilteredFlightResults,
  ]);

  useEffect(() => {
    console.log("filteredFlightResults ", filteredFlightResults);
  }, [filteredFlightResults]);

  const handleFetchFlights = async (pollingGeneration: number) => {
    let REQUEST_COUNT = 0;
    let consecutiveFailures = 0;
    let hasReceivedResponse = false;
    const config = pollingConfig;

    // Datas de ida que já passaram ficam de fora; a SERP só bloqueia a busca
    // quando todas passaram (ver o efeito que chama `handleFetchFlights`).
    const departureDates = filterPastDepartureDates(searchState.departureDates);

    const requestLimit = getPollingRequestLimit(
      departureDates,
      searchState.returnDates,
      config,
    );
    const pollingInterval = getPollingInterval(config);

    const payload = {
      searchIntent: {
        originAirport: searchState?.origin?.code,
        destinationAirport: searchState?.destination?.code,
        departureDate: departureDates,
        returnDate: searchState.returnDates,
        quantityOfAdults: searchState.adults,
        quantityOfChildrens: searchState.children,
        quantityOfBabies: searchState.babies,
      },
    };

    setSearchLoader(REQUEST_COUNT, requestLimit);

    const poll = async () => {
      if (pollingGeneration !== pollingGenerationRef.current) {
        return;
      }

      if (pollingRequestRef.current) {
        await pollingRequestRef.current.catch(() => undefined);
        await new Promise((resolve) => setTimeout(resolve, 0));

        if (pollingGeneration !== pollingGenerationRef.current) {
          return;
        }
      }

      const isFinished =
        dataRef.current !== null && isSearchIntentFinished(dataRef.current);

      if (REQUEST_COUNT >= requestLimit || isFinished) {
        console.log(
          `Busca Finalizada ${REQUEST_COUNT}/${requestLimit}`,
          flightResults,
        );
        return;
      }

      REQUEST_COUNT++;
      setSearchLoader(REQUEST_COUNT, requestLimit);

      let pollingOutcome: SearchFlightsResult | null = null;
      try {
        console.log(
          `Enviando Request ${REQUEST_COUNT}/${requestLimit}`,
          payload,
        );

        const pollingRequest = searchFlights(payload, {
          isFirstSearchRequest: REQUEST_COUNT === 1,
          isLastSearchRequest: REQUEST_COUNT === requestLimit,
          pollingNumber: REQUEST_COUNT,
        });

        pollingRequestRef.current = pollingRequest;
        pollingRequestGenerationRef.current = pollingGeneration;

        pollingOutcome = await pollingRequest;

        console.log(`Recebendo Response ${REQUEST_COUNT}/${requestLimit}`);
      } catch (error) {
        captureSentryException(error, {
          tags: {
            feature: "results-flights",
            operation: "searchFlightsPolling",
          },
          contexts: {
            flightSearch: {
              requestNumber: REQUEST_COUNT,
              requestLimit,
            },
          },
        });
        console.error(`Erro ${REQUEST_COUNT}/${requestLimit}`, error);
      } finally {
        if (pollingRequestGenerationRef.current === pollingGeneration) {
          pollingRequestRef.current = null;
        }
      }

      if (pollingGeneration !== pollingGenerationRef.current) {
        return;
      }

      if (pollingOutcome?.ok) {
        consecutiveFailures = 0;
        hasReceivedResponse = true;
      } else {
        consecutiveFailures += 1;

        // Repetir a mesma chamada até o limite de polls não ajuda quando o erro
        // é definitivo (4xx) ou se repete: mostra o erro e para.
        if (
          REQUEST_COUNT >= requestLimit ||
          shouldStopPolling({
            error: pollingOutcome?.error,
            consecutiveFailures,
            hasReceivedResponse,
          })
        ) {
          // Com resultados já na tela a busca termina com o que chegou, sem
          // esconder a lista atrás de um erro.
          if (dataRef.current) {
            setIsSearchFinished(true);
          } else {
            setSearchFailure("request-failed");
          }
          return;
        }
      }

      if (
        pollingGeneration !== pollingGenerationRef.current ||
        REQUEST_COUNT >= requestLimit
      ) {
        return;
      }

      pollingTimeoutRef.current = setTimeout(() => {
        pollingTimeoutRef.current = null;
        void poll();
      }, pollingInterval);
    };

    void poll();
  };

  const isSearchIntentFinished = (
    response: CreateSearchIntentResponse,
  ): boolean => {
    return response.message.wsChannels.every((channel) => {
      // Verifica se todos os status dos canais estão como "finished"
      const isChannelFinished = channel.status === "finished";

      // Verifica se todos os status dos cacheData estão como "finished"
      const isCacheDataFinished = Object.values(channel.cacheData).every(
        (cache) => cache.status === "finished",
      );

      // Atualiza o estado
      setIsSearchFinished(isChannelFinished || isCacheDataFinished);
      return isChannelFinished || isCacheDataFinished;
    });
  };

  useEffect(() => {
    if (searchState.searchLoaderCurrentStep > 0) {
      const searchPercentResult =
        (searchState.searchLoaderCurrentStep * 100) /
        searchState.searchLoaderMaxSteps;

      setSearchPercentResult(
        searchState.isSearchFinished ? 100 : searchPercentResult,
      );
    } else {
      let elapsedTime = 0;
      let localPercent = 0;
      const initialProgressLimit = Math.trunc(
        100 / searchState.searchLoaderMaxSteps,
      );

      const interval = setInterval(() => {
        if (elapsedTime >= 3000 || localPercent >= initialProgressLimit) {
          clearInterval(interval);
        } else {
          localPercent += 1;
          elapsedTime += 300;
          setSearchPercentResult(localPercent);
        }
      }, 300);

      return () => clearInterval(interval);
    }
  }, [
    searchState.searchLoaderCurrentStep,
    searchState.searchLoaderMaxSteps,
    searchState.isSearchFinished,
  ]);

  const updateFlightResults = async (results: CreateSearchIntentResponse) => {
    console.log("[debug]: 0.2) Atualizando FlightResults com:", results);
    const parsedResults = await parseCreateSearchIntentToSERPResult(
      results,
      flightResults,
    );
    console.log("[debug]: 1) Resultados após o parsing:", parsedResults);
    setFlightResults(parsedResults);
  };

  const updateFiltersFromResults = (parsedResults: SERPResult) => {
    console.log("[debug]: 2) Atualizando filtros com:", parsedResults);

    // Preços
    setPriceRange([
      parsedResults?.predefinedFilters?.priceFrom,
      parsedResults?.predefinedFilters?.priceTo,
    ]);
    setMinPrice(parsedResults?.predefinedFilters?.priceFrom);
    setMaxPrice(parsedResults?.predefinedFilters?.priceTo);

    // Aeroportos de Origem
    setAirportsOrigin(
      parseAirports(parsedResults?.predefinedFilters.airportsFrom ?? []),
    );
    setAirportsFrom(
      parseAirports(parsedResults?.predefinedFilters.airportsFrom) ?? [],
    );

    // Aeroportos de Destino
    setAirportsDestination(
      parseAirports(parsedResults?.predefinedFilters.airportsTo ?? []),
    );
    setAirportsTo(
      parseAirports(parsedResults?.predefinedFilters.airportsTo) ?? [],
    );

    // Cias Aéreas

    const airlinesList = parsedResults?.predefinedFilters?.airlines ?? [];
    const parsedAirlines = parseAirlines(airlinesList);
    if (Array.isArray(parsedAirlines)) {
      setAirlines(parsedAirlines);
    }

    // Sellers
    const sellersList = parsedResults?.predefinedFilters?.sellers ?? [];
    const parsedSellers = parseSellers(sellersList);
    setSellers(parsedSellers);
  };

  const handleFlightResults = async (results: CreateSearchIntentResponse) => {
    console.log("[debug]: 0.1) handleFlightResults chamada com:", results);
    await updateFlightResults(results);

    // Após garantir que os resultados foram atualizados
    setFlightResults((parsedResults) => {
      if (parsedResults) {
        updateFiltersFromResults(parsedResults);
        setFilteredFlightResults(parsedResults);
      }
      return parsedResults;
    });
  };

  useEffect(() => {
    console.log(
      "[test]: 2) Estado atual de filteredFlightResults:",
      filteredFlightResults,
    );
  }, [filteredFlightResults]);

  useEffect(() => {
    if (
      data &&
      pollingRequestGenerationRef.current !== pollingGenerationRef.current
    ) {
      console.log("[debug]: Ignorando resposta de busca anterior");
      return;
    }

    dataRef.current = data;
    console.log("[debug]: 00) Conteúdo de data recebido do hook:", data);

    if (dataRef.current) {
      console.log(
        "[debug]: 0) Conteúdo de data recebido do hook:",
        dataRef.current,
      );
      handleFlightResults(dataRef.current);
    } else {
      console.log("[debug]: 1) Nenhum dado recebido do useFlightSearch");
    }
  }, [data]);

  // Observa flightResults e aplica filtros automaticamente
  useEffect(() => {
    if (flightResults) {
      console.log("[debug]: 3) flightResults atualizado:", flightResults);
      updateFiltersFromResults(flightResults);
    }
  }, [flightResults]);

  const submittedSearchKey = `${location.pathname}${location.search}`;

  useEffect(() => {
    searchExecuted.current = false;
    pollingGenerationRef.current += 1;

    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  }, [submittedSearchKey]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const departureDates = searchParams.get("departure-date")?.split(",") ?? [];
    const returnDates = searchParams.get("return-date")?.split(",") ?? [];
    const isSearchStateSyncedWithUrl =
      searchState.origin?.code === searchParams.get("origin-airport") &&
      searchState.destination?.code ===
        searchParams.get("destination-airport") &&
      JSON.stringify(searchState.departureDates ?? []) ===
        JSON.stringify(departureDates) &&
      JSON.stringify(searchState.returnDates ?? []) ===
        JSON.stringify(returnDates) &&
      searchState.adults ===
        Number(searchParams.get("quantity-of-adults") ?? 1) &&
      searchState.children ===
        Number(searchParams.get("quantity-of-children") ?? 0) &&
      searchState.babies ===
        Number(searchParams.get("quantity-of-babies") ?? 0);
    const hasExecutedCurrentSearch =
      searchExecuted.current &&
      executedSearchKeyRef.current === submittedSearchKey;

    if (
      !hasExecutedCurrentSearch &&
      isSearchStateSyncedWithUrl &&
      searchState &&
      searchState.origin &&
      searchState.destination &&
      searchState.departureDates &&
      searchState.returnDates
    ) {
      searchExecuted.current = true;
      executedSearchKeyRef.current = submittedSearchKey;
      dataRef.current = null;
      setIsSearchFinished(false);
      setStateSearchFinished(false);
      setSearchFailure(null);

      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }

      const pollingGeneration = pollingGenerationRef.current + 1;
      pollingGenerationRef.current = pollingGeneration;

      if (hasOnlyPastDepartureDates(searchState.departureDates)) {
        // Todas as datas de ida já passaram: a API recusa (HTTP 400). Nem chama.
        setFlightResults(null);
        setFilteredFlightResults(null);
        setSearchFailure("past-date");
        return;
      }

      void handleFetchFlights(pollingGeneration);
    }
  }, [submittedSearchKey, searchRetryNonce]);

  const handleRetrySearch = () => {
    setSearchFailure(null);
    searchExecuted.current = false;
    executedSearchKeyRef.current = null;
    setSearchRetryNonce((nonce) => nonce + 1);
  };

  useEffect(() => {
    return () => {
      pollingGenerationRef.current += 1;

      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    console.log(
      "[test]: 4) useEffect isSearchFinished disparado com estado:",
      isSearchFinished,
    );

    if (isSearchFinished) {
      console.log("busca finalizada");

      setStateSearchFinished(true);
      setFilteredFlightResults(flightResults);
    }
  }, [isSearchFinished]);

  const handleChangePage = (page: number) => {
    setFilteredFlightResults((currentResults) => {
      if (
        !currentResults?.pagination ||
        currentResults.pagination.currentPage === page
      ) {
        return currentResults;
      }

      return {
        ...currentResults,
        pagination: { ...currentResults.pagination, currentPage: page },
      };
    });
  };

  const applyFilters = () => {
    console.log(
      "[test]: 3): Resultados antes dos filtros:",
      flightResults?.results?.length,
    );

    if (!filteredFlightResults?.appliedFilters) {
      return;
    }

    const {
      priceFrom,
      priceTo,
      airportsFrom,
      airportsTo,
      sellers,
      firtsSectorDepartureTimeFrom,
      firtsSectorDepartureTimeTo,
      secondSectorDepartureTimeFrom,
      secondSectorDepartureTimeTo,
      airlines,
    } = filteredFlightResults.appliedFilters;

    let filtered = flightResults?.results ?? [];
    // let filtered = flightResults?.results.slice(0, 1) ?? [];

    // ✅ Filtra pelo número de conexões
    filtered = filtered.filter((card) =>
      card.sectors.every((sector) => stops.includes(sector.stops)),
    );

    console.log(
      `[applyFilters]: priceFrom = ${priceFrom} e priceTo = ${priceTo}`,
    );

    // ✅ Filtra os sellers pela faixa e atualiza o preço destacado do card
    if (filtered.length) {
      filtered = filtered
        .map((result) => {
          const filteredSellers = result.sellers
            .filter(
              (seller) => seller.price >= priceFrom && seller.price <= priceTo,
            )
            .sort((a, b) => a.price - b.price);

          return {
            ...result,
            sellers: filteredSellers,
            minPrice: filteredSellers[0]?.price ?? result.minPrice,
          };
        })
        .filter((result) => result.sellers.length > 0);
    }

    // ✅ Filtra pelo aeroporto de origem
    if (filtered.length) {
      filtered = filtered.filter((card) =>
        airportsFrom.includes(card.sectors[0]?.departureAirportIata),
      );
    }

    // ✅ Filtra pelo aeroporto de destino
    if (filtered.length) {
      filtered = filtered.filter((card) => {
        const firstSector = card.sectors[0];
        const firstFlight =
          firstSector?.flights?.[firstSector.flights.length - 1];

        return (
          firstFlight && airportsTo.includes(firstFlight.arrivalAirportIata)
        );
      });
    }

    // ✅ Aplica filtro de horário de partida SOMENTE SE os valores forem definidos
    const isFirstSectorTimeFilterValid =
      typeof firtsSectorDepartureTimeFrom === "number" &&
      typeof firtsSectorDepartureTimeTo === "number";

    if (filtered.length && isFirstSectorTimeFilterValid) {
      filtered = filtered.filter((card) => {
        const firstSector = card.sectors[0]; // Primeiro setor (ida)
        let departureTime: Date | string = firstSector?.departureTime;

        if (!departureTime) return false;

        if (departureTime instanceof Date) {
          departureTime = departureTime.toISOString();
        }

        if (typeof departureTime !== "string" || !departureTime.includes("T")) {
          console.error(
            "[applyFilters]: ⚠️ departureTime inválido:",
            departureTime,
          );
          return false;
        }

        const getLocalTimeMinutes = (departureTime: string) => {
          const date = new Date(departureTime);
          return date.getHours() * 60 + date.getMinutes();
        };

        const departureMinutes = getLocalTimeMinutes(departureTime);
        return (
          departureMinutes >= firtsSectorDepartureTimeFrom &&
          departureMinutes <= firtsSectorDepartureTimeTo
        );
      });
    } else {
      console.log(
        "[applyFilters]: ❌ Filtro de horário de ida ignorado (valores indefinidos)",
      );
    }

    // ✅ Aplica filtro de horário de partida na volta SOMENTE SE os valores forem definidos e houver volta
    const isSecondSectorTimeFilterValid =
      typeof secondSectorDepartureTimeFrom === "number" &&
      typeof secondSectorDepartureTimeTo === "number";

    if (filtered.length && isSecondSectorTimeFilterValid) {
      filtered = filtered.filter((card) => {
        const secondSector = card.sectors[1]; // Verifica se há setor de volta
        if (!secondSector) return true; // Se não houver, não filtra por horário de volta (é uma busca só de ida)

        let departureTime: Date | string = secondSector?.departureTime;

        if (!departureTime) return false;

        if (departureTime instanceof Date) {
          departureTime = departureTime.toISOString();
        }

        if (typeof departureTime !== "string" || !departureTime.includes("T")) {
          console.error(
            "[applyFilters]: ⚠️ departureTime inválido:",
            departureTime,
          );
          return false;
        }

        const getLocalTimeMinutes = (departureTime: string) => {
          const date = new Date(departureTime);
          return date.getHours() * 60 + date.getMinutes();
        };

        const departureMinutes = getLocalTimeMinutes(departureTime);

        return (
          departureMinutes >= secondSectorDepartureTimeFrom &&
          departureMinutes <= secondSectorDepartureTimeTo
        );
      });

      console.log(
        "[applyFilters]: Após filtro de horário de partida:",
        filtered.length,
      );
    } else {
      console.log(
        "[applyFilters]: ❌ Filtro de horário de volta ignorado (valores indefinidos ou busca só de ida)",
      );
    }
    // ✅ Filtra pelas companhias aéreas
    if (filtered.length) {
      filtered = filtered.filter((result) =>
        matchesAirlineFilter(result, airlines),
      );
    }
    console.log(
      "[applyFilters]: Após filtro de companhias aéreas:",
      filtered.length,
    );

    console.log("[Sellers Filter] 1 : filtered ->", JSON.stringify(filtered));
    console.log("[Sellers Filter] 2 : sellers ->", JSON.stringify(sellers));

    // ✅ Filtra pelos sellers
    if (filtered.length) {
      if (!sellers.length) {
        filtered = [];
      } else {
        const validSellers = sellers.filter((s) => s !== "");

        filtered = filtered
          .map((result) => {
            const filteredSellers = result.sellers
              .filter((seller) => validSellers.includes(seller.sellerId))
              .sort((a, b) => a.price - b.price);

            return {
              ...result,
              sellers: filteredSellers,
              minPrice: filteredSellers[0]?.price ?? result.minPrice,
            };
          })
          .filter((result) => result.sellers.length > 0);
      }
    }
    console.log("[applyFilters]: Após filtro de sellers:", filtered.length);

    // ✅ Ordena os voos pelo menor preço após o filtro
    if (filtered.length) {
      filtered = filtered.sort((a, b) => a.minPrice - b.minPrice);
    }

    console.log(
      "[applyFilters]: Total de voos após todos os filtros:",
      filtered.length,
    );

    setFilteredFlightResults((currentResults) => {
      if (!currentResults?.pagination) return currentResults;

      return {
        ...currentResults,
        results: filtered ?? [],
        pagination: {
          ...currentResults.pagination,
          total: filtered.length,
          totalPages: Math.ceil(
            filtered.length / currentResults.pagination.maxItemsPerPage,
          ),
        },
      };
    });
  };

  useEffect(() => {
    console.log("1) applyFilters");
    applyFilters();
  }, [filteredFlightResults?.appliedFilters]);

  const orderedFlightResults = useMemo(() => {
    const results = filteredFlightResults?.results ?? [];
    const indexedResults = results.map((result, index) => ({ result, index }));

    if (resultOrder === "cheapest") {
      return indexedResults
        .sort(
          (a, b) => a.result.minPrice - b.result.minPrice || a.index - b.index,
        )
        .map(({ result }) => result);
    }

    if (resultOrder === "fastest") {
      return indexedResults
        .sort(
          (a, b) =>
            getResultDuration(a.result) - getResultDuration(b.result) ||
            a.result.minPrice - b.result.minPrice ||
            a.index - b.index,
        )
        .map(({ result }) => result);
    }

    return orderResultsByBestOption(results);
  }, [filteredFlightResults?.results, resultOrder]);

  const handleResultOrderChange = (order: SearchOrder) => {
    setResultOrder(order);
    if (filteredFlightResults?.pagination.currentPage !== 1) {
      setFilteredFlightResults({
        ...filteredFlightResults,
        pagination: {
          ...filteredFlightResults.pagination,
          currentPage: 1,
        },
      });
    }
  };

  return (
    <ContainerResultsFlights>
      {isVisibleFilterModal && (
        <FilterModal
          isVisibleModal={isVisibleFilterModal}
          onClose={() => setIsVisibleFilterModal(false)}
        />
      )}

      <div id="header-filter-mobile" ref={headerFilterRef} />
      {!desktop && (
        <HeaderFilterMobile
          onOpenFilterModal={() => setIsVisibleFilterModal(true)}
          selectedOrder={resultOrder}
          onOrderChange={handleResultOrderChange}
        />
      )}
      <AlertSerpCta isMobile={!desktop} />
      <StyledResultsHeader isMobile={!desktop}>
        {desktop && <FlightDetailsHeader />}

        {(!searchFailure || !!filteredFlightResults?.results?.length) && (
          <ProgressBar
            key={0}
            currentProgress={searchState.searchPercentResult}
            isMobile={!desktop}
            selectedOrder={resultOrder}
            onOrderChange={handleResultOrderChange}
          />
        )}
      </StyledResultsHeader>
      <StyledResultsFlights isMobile={!desktop}>
        {desktop && (
          <StyledFilterContainer>
            <StopsFilter
              stops={stopsOptions}
              selectedStops={stops ?? [0]}
              onChange={handleStopsChange}
            />

            <RangeFilter
              title="Preço"
              iconName="FaRegMoneyBillAlt"
              minValue={minPrice}
              maxValue={maxPrice}
              valueRange={priceRange}
              handleValueChange={handlePriceChange}
              value={priceRange}
              type="currency"
            />

            {/* {JSON.stringify(airportsFrom)} */}
            <AirportsFilter
              type="origin"
              airports={airportsFrom}
              originAirport="Partindo de"
              onChange={handleAirportOriginChange}
              onToggleAll={handleAllAirportOrigins}
            />

            {/* {JSON.stringify(filteredFlightResults?.appliedFilters?.airportsTo)} */}
            <AirportsFilter
              type="destination"
              airports={airportsTo}
              originAirport="Chegando em"
              onChange={handleAirportDestinationChange}
              onToggleAll={handleAllAirportDestinations}
            />

            <RangeFilter
              title="Horário de ida"
              iconName="LuArrowUpRight"
              minValue={0}
              maxValue={1439}
              valueRange={firstSectorTimeRange}
              handleValueChange={handleFisrtSectorDepartureTimeChange}
              value={firstSectorTimeRange}
              type="time"
            />

            <RangeFilter
              title="Horário de volta"
              iconName="LuArrowDownLeft"
              minValue={0}
              maxValue={1439}
              valueRange={secondSectorTimeRange}
              handleValueChange={handleSecondSectorDepartureTimeChange}
              value={secondSectorTimeRange}
              type="time"
            />

            <EntityFilter
              entities={Array.isArray(airlines) ? airlines : []}
              title="Companhias Aéreas"
              iconName="MdOutlineAirlines"
              onChange={handleAirlineChange}
              onToggleAll={handleAllAirlines}
            />
            <EntityFilter
              entities={sellers}
              title="Vendedores"
              iconName="LuTicket"
              onChange={handleSellerChange}
              onToggleAll={handleAllSellers}
              hasBorder={false}
            />
          </StyledFilterContainer>
        )}
        {!isSearchFinished &&
          !searchFailure &&
          !filteredFlightResults?.results?.length && (
            <FlightCardSkeleton isMobile={!desktop} />
          )}

        {searchFailure === "past-date" &&
          !filteredFlightResults?.results?.length && (
            <SearchErrorContent
              title="Essa data já passou"
              description={`A data de ida (${pastDepartureDateLabel}) é anterior a hoje. Escolha novas datas na busca para ver os voos.`}
            />
          )}

        {searchFailure === "request-failed" &&
          !flightResults?.results?.length && (
            <SearchErrorContent
              title="Não foi possível carregar os resultados"
              description="A busca está com problema no momento. Tente novamente em instantes."
              actionLabel="Tentar novamente"
              onAction={handleRetrySearch}
            />
          )}

        {isSearchFinished && !filteredFlightResults?.results?.length && (
          <Suspense fallback={null}>
            <NoFlightsContent />
          </Suspense>
        )}

        {filteredFlightResults?.results &&
          filteredFlightResults.results.length > 0 && (
            <StyledFlightCardsContainer isMobile={!desktop}>
              {inlineData && <InlineBanner inlineData={inlineData} />}
              {orderedFlightResults
                .slice(
                  (filteredFlightResults.pagination.currentPage - 1) *
                    filteredFlightResults?.pagination.maxItemsPerPage,
                  filteredFlightResults.pagination.currentPage *
                    filteredFlightResults?.pagination.maxItemsPerPage,
                )
                .map((result, index) => {
                  const resultKey = getFlightResultKey(result);

                  return desktop ? (
                    <FlightCard
                      cardResult={result}
                      key={resultKey}
                      resultPosition={{
                        indexPosition: index,
                        currentPage:
                          filteredFlightResults?.pagination?.currentPage,
                      }}
                    />
                  ) : (
                    <FlightCardMobile
                      cardResult={result}
                      key={resultKey}
                      resultPosition={{
                        indexPosition: index,
                        currentPage:
                          filteredFlightResults?.pagination?.currentPage,
                      }}
                    />
                  );
                })}

              <PaginationComponent
                changePage={handleChangePage}
                pagination={filteredFlightResults.pagination}
              />
            </StyledFlightCardsContainer>
          )}

        {desktop && (
          <StyledRigthAdsContainer>
            <AdBannerContainer
              slotId="div-gpt-ad-1748864115044-0"
              adUnit="/27140001/2024-home-desktop-conteudos-300x250-A"
              sizes={[[300, 250]]}
              marginBottom={0}
            />
            <AdBannerContainer
              slotId="div-gpt-ad-1748864136940-0"
              adUnit="/27140001/2024-home-desktop-conteudos-300x250-B"
              sizes={[[300, 250]]}
              marginBottom={0}
            />
          </StyledRigthAdsContainer>
        )}
      </StyledResultsFlights>
    </ContainerResultsFlights>
  );
});
